import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Gift, ArrowLeft, RefreshCw, Trash2, Plus, Check } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const emptyForm = { name: '', description: '', points_cost: 100, image_url: '', is_active: true, stock: '', sort_order: 0 };

export default function RewardsAdminPage() {
  const navigate = useNavigate();
  const [rewards, setRewards] = useState([]);
  const [redemptions, setRedemptions] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        axios.get(`${API}/rewards`, authHeader()),
        axios.get(`${API}/rewards/redemptions/all`, authHeader()),
      ]);
      setRewards(r1.data || []);
      setRedemptions(r2.data || []);
    } catch {
      toast.error('Prämien konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const resetForm = () => { setForm(emptyForm); setEditingId(null); };

  const startEdit = (r) => {
    setEditingId(r.id);
    setForm({
      name: r.name || '',
      description: r.description || '',
      points_cost: r.points_cost || 0,
      image_url: r.image_url || '',
      is_active: r.is_active !== false,
      stock: r.stock == null ? '' : r.stock,
      sort_order: r.sort_order || 0,
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error('Bitte einen Namen angeben'); return; }
    if (!form.points_cost || Number(form.points_cost) < 1) { toast.error('Punktekosten müssen mindestens 1 sein'); return; }
    setSaving(true);
    const payload = {
      name: form.name.trim(),
      description: form.description?.trim() || null,
      points_cost: Number(form.points_cost),
      image_url: form.image_url?.trim() || null,
      is_active: !!form.is_active,
      stock: form.stock === '' || form.stock === null ? null : Number(form.stock),
      sort_order: Number(form.sort_order) || 0,
    };
    try {
      if (editingId) {
        await axios.put(`${API}/rewards/${editingId}`, payload, authHeader());
        toast.success('Prämie aktualisiert');
      } else {
        await axios.post(`${API}/rewards`, payload, authHeader());
        toast.success('Prämie erstellt');
      }
      resetForm();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Diese Prämie wirklich löschen?')) return;
    try {
      await axios.delete(`${API}/rewards/${id}`, authHeader());
      toast.success('Prämie gelöscht');
      if (editingId === id) resetForm();
      load();
    } catch {
      toast.error('Löschen fehlgeschlagen');
    }
  };

  const toggleActive = async (r) => {
    try {
      await axios.put(`${API}/rewards/${r.id}`, {
        name: r.name,
        description: r.description || null,
        points_cost: r.points_cost,
        image_url: r.image_url || null,
        is_active: !r.is_active,
        stock: r.stock == null ? null : r.stock,
        sort_order: r.sort_order || 0,
      }, authHeader());
      load();
    } catch {
      toast.error('Status konnte nicht geändert werden');
    }
  };

  const fulfill = async (id) => {
    try {
      await axios.post(`${API}/rewards/redemptions/${id}/fulfill`, {}, authHeader());
      toast.success('Als erfüllt markiert');
      load();
    } catch {
      toast.error('Aktion fehlgeschlagen');
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-4 md:p-6 space-y-6" data-testid="rewards-admin-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="rewards-back-btn">
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <Gift className="h-6 w-6 text-primary" />
        <div className="flex-1">
          <h1 className="text-2xl font-semibold">Prämien</h1>
          <p className="text-sm text-muted-foreground">Einlösbare Prämien, die Kunden mit Punkten in der App erhalten.</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="rewards-refresh-btn">
          <RefreshCw className="h-4 w-4 mr-2" /> Aktualisieren
        </Button>
      </div>

      {/* Formular */}
      <Card>
        <CardHeader>
          <CardTitle>{editingId ? 'Prämie bearbeiten' : 'Neue Prämie'}</CardTitle>
          <CardDescription>Name, Punktekosten und optional Beschreibung, Bild und Bestand.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="r-name">Name</Label>
              <Input id="r-name" data-testid="reward-name-input" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="z.B. Gratis Shake" />
            </div>
            <div>
              <Label htmlFor="r-cost">Punktekosten</Label>
              <Input id="r-cost" type="number" data-testid="reward-cost-input" value={form.points_cost}
                onChange={(e) => setForm({ ...form, points_cost: e.target.value })} />
            </div>
          </div>
          <div>
            <Label htmlFor="r-desc">Beschreibung (optional)</Label>
            <Textarea id="r-desc" data-testid="reward-desc-input" value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} />
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="r-img">Bild-URL (optional)</Label>
              <Input id="r-img" data-testid="reward-image-input" value={form.image_url}
                onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="https://..." />
            </div>
            <div>
              <Label htmlFor="r-stock">Bestand (leer = unbegrenzt)</Label>
              <Input id="r-stock" type="number" data-testid="reward-stock-input" value={form.stock}
                onChange={(e) => setForm({ ...form, stock: e.target.value })} placeholder="∞" />
            </div>
            <div>
              <Label htmlFor="r-sort">Sortierung</Label>
              <Input id="r-sort" type="number" data-testid="reward-sort-input" value={form.sort_order}
                onChange={(e) => setForm({ ...form, sort_order: e.target.value })} />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Switch id="r-active" checked={form.is_active} data-testid="reward-active-switch"
              onCheckedChange={(v) => setForm({ ...form, is_active: v })} />
            <Label htmlFor="r-active">In der App sichtbar</Label>
          </div>
          <div className="flex gap-2">
            <Button onClick={save} disabled={saving} data-testid="reward-save-btn">
              <Plus className="h-4 w-4 mr-2" /> {editingId ? 'Speichern' : 'Prämie hinzufügen'}
            </Button>
            {editingId && (
              <Button variant="outline" onClick={resetForm} data-testid="reward-cancel-btn">Abbrechen</Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Liste */}
      <Card>
        <CardHeader><CardTitle>Katalog ({rewards.length})</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {loading && <p className="text-sm text-muted-foreground">Lädt…</p>}
          {!loading && rewards.length === 0 && (
            <p className="text-sm text-muted-foreground" data-testid="rewards-empty">Noch keine Prämien angelegt.</p>
          )}
          {rewards.map((r) => (
            <div key={r.id} data-testid={`reward-row-${r.id}`}
              className="flex items-center gap-3 border rounded-lg p-3">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium">{r.name}</span>
                  <Badge variant="secondary">{r.points_cost} Pkt</Badge>
                  {!r.is_active && <Badge variant="outline">inaktiv</Badge>}
                  {r.stock != null && <Badge variant="outline">Bestand: {r.stock}</Badge>}
                </div>
                {r.description && <p className="text-sm text-muted-foreground">{r.description}</p>}
              </div>
              <Switch checked={r.is_active !== false} onCheckedChange={() => toggleActive(r)}
                data-testid={`reward-toggle-${r.id}`} />
              <Button variant="ghost" size="sm" onClick={() => startEdit(r)} data-testid={`reward-edit-${r.id}`}>Bearbeiten</Button>
              <Button variant="ghost" size="icon" onClick={() => remove(r.id)} data-testid={`reward-delete-${r.id}`}>
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Einlösungen */}
      <Card>
        <CardHeader>
          <CardTitle>Einlösungen ({redemptions.length})</CardTitle>
          <CardDescription>Von Kunden eingelöste Prämien – als erfüllt markieren, wenn übergeben.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {redemptions.length === 0 && (
            <p className="text-sm text-muted-foreground" data-testid="redemptions-empty">Noch keine Einlösungen.</p>
          )}
          {redemptions.map((d) => (
            <div key={d.id} data-testid={`redemption-row-${d.id}`}
              className="flex items-center gap-3 border rounded-lg p-3">
              <div className="flex-1">
                <span className="font-medium">{d.reward_name}</span>
                <span className="text-sm text-muted-foreground"> · {d.customer_name || 'Kunde'} · {d.points_cost} Pkt</span>
              </div>
              {d.status === 'fulfilled' ? (
                <Badge variant="secondary"><Check className="h-3 w-3 mr-1" /> Erfüllt</Badge>
              ) : (
                <Button size="sm" onClick={() => fulfill(d.id)} data-testid={`redemption-fulfill-${d.id}`}>
                  Als erfüllt markieren
                </Button>
              )}
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
